import { ApisauceInstance, create, ApiResponse } from 'apisauce';
import { getGeneralApiProblem } from './api-problem';
import { ApiConfig, DEFAULT_API_CONFIG } from './api-config';
import type * as Types from './api.types';
import type { ApiProps } from './api.props';
import { paginatedRequestTransformer } from "../transformers/request-transformer";
import { paginationTransformer } from '../transformers/pagination-transformer';
/**
 * Manages all requests to the API.
 */
export class Api {
  /**
   * The underlying apisauce instance which performs the requests.
   */
  apisauce!: ApisauceInstance;

  /**
   * Configurable options.
   */
  config: ApiConfig;

  /**
   * Creates the api.
   *
   * @param config The configuration to use.
   */
  constructor(config: ApiConfig = DEFAULT_API_CONFIG) {
    this.config = config;
  }

  /**
   * Sets up the API.  This will be called during the bootup
   * sequence and will happen before the first React component
   * is mounted.
   *
   * Be as quick as possible in here.
   */
  setup(apiConfig: ApiProps) {
    // construct the apisauce instance
    this.apisauce = create({
      baseURL: apiConfig.serverBaseUrl,
      timeout: this.config.timeout,
      headers: {
        Accept: 'application/json',
        jwt: apiConfig.jwt,
        client: apiConfig.clientId,
        clientUserId: apiConfig.clientUserId        
      },
    });
    
  }

  async getRequests(userId?:number, url?:string|null): Promise<Types.GetKiaRequestResult>{
    const endPoint = url || "api/client-user-request/"+userId
    const response: ApiResponse<any> = await this.apisauce.get(endPoint);

    if(!response.ok){
      const problem = getGeneralApiProblem(response);
      if(problem) return problem;      
    }

    console.log(response);
    return {kind:"ok", requests: paginatedRequestTransformer(response), pagination: paginationTransformer(response)};
  }

  /**
   * Gets a list of users.
   */
  async getUsers(): Promise<Types.GetUsersResult> {
    // make the api call
    const response: ApiResponse<any> = await this.apisauce.get(`/users`);

    // the typical ways to die when calling an api
    if (!response.ok) {
      const problem = getGeneralApiProblem(response);
      if (problem) return problem;
    }

    const convertUser = (raw: { id: any; name: any }) => {
      return {
        id: raw.id,
        name: raw.name,
      };
    };

    // transform the data into the format we are expecting
    try {
      const rawUsers = response.data;
      const resultUsers: Types.User[] = rawUsers.map(convertUser);
      return { kind: 'ok', users: resultUsers };
    } catch {
      return { kind: 'bad-data' };
    }
  }

  /**
   * Gets a single user by ID
   */

  async getUser(id: string): Promise<Types.GetUserResult> {
    // make the api call
    const response: ApiResponse<any> = await this.apisauce.get(`/users/${id}`);

    // the typical ways to die when calling an api
    if (!response.ok) {
      const problem = getGeneralApiProblem(response);
      if (problem) return problem;
    }

    // transform the data into the format we are expecting
    try {
      const resultUser: Types.User = {
        id: response.data.id,
        name: response.data.name,
      };
      return { kind: 'ok', user: resultUser };
    } catch {
      return { kind: 'bad-data' };
    }
  }
}
